/**
 * Witnessed Fire — pure game engine (no React, no DOM, no unseeded randomness).
 *
 * The structural lesson is PERMIT: an agent's tool call executes only when it
 * carries a witnessed capability for that tool. The player holds a governed
 * execution lane; tool-call requests approach it. Firing at a request launches
 * a permit projectile — but only a witnessed call has a shot to fire. For an
 * unwitnessed call the fire intent produces no projectile at all: the shot does
 * not exist, so the call is structurally inexpressible, not blocked after the
 * fact. Unwitnessed calls are not "shot down"; there was never a round for them.
 *
 * Spawning is deterministic given a seed and the tick sequence, so the model is
 * reproducible and testable. The arcade layer (combo, score, flashes) lives in
 * the panel and never changes the PERMIT verdict computed here.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

export type ToolId = string;

/** A tool the agent may be permitted to call, shown in the permit palette. */
export interface ToolDef {
  readonly id: ToolId;
  readonly label: string;
  readonly glyph: string;
}

/** A template for the kind of tool-call requests a wave spawns. */
export interface CallTemplate {
  readonly toolId: ToolId;
  /**
   * Whether requests from this template arrive carrying a witnessed capability.
   * An unwitnessed request can never be fired on — it has no permit round.
   */
  readonly witnessed: boolean;
  /** Lane (row) index the request travels along. */
  readonly lane: number;
}

/** A wave (level): its tools, request templates, cadence, and quota. */
export interface FireWave {
  readonly id: string;
  readonly name: string;
  readonly brief: string;
  readonly lanes: number;
  readonly tools: readonly [ToolDef, ...ToolDef[]];
  readonly templates: readonly [CallTemplate, ...CallTemplate[]];
  /** Ticks between successive spawns. */
  readonly spawnEveryTicks: number;
  /** Distance (in lane units) a request travels before it reaches execution. */
  readonly laneLength: number;
  /** Distance a request advances per tick. */
  readonly approachPerTick: number;
  /** Number of witnessed calls to permit (fire on) to clear the wave. */
  readonly quota: number;
  /** How many witnessed calls may reach execution unpermitted before failure. */
  readonly breachBudget: number;
}

/** A live tool-call request approaching the execution lane. */
export interface CallRequest {
  readonly id: string;
  readonly toolId: ToolId;
  readonly witnessed: boolean;
  readonly lane: number;
  /** Distance remaining to the execution boundary; 0 means it arrived. */
  readonly distance: number;
}

export type FireStatus = "playing" | "cleared" | "breached";

export type FireEvent = "none" | "permitted" | "no-shot" | "breach" | "passed";

export interface FireState {
  readonly waveId: string;
  readonly seed: number;
  readonly tick: number;
  readonly nextSpawnTick: number;
  readonly spawnCount: number;
  readonly requests: readonly CallRequest[];
  readonly status: FireStatus;
  readonly permitted: number;
  readonly breaches: number;
  readonly lastEvent: FireEvent;
  readonly lastToolId: ToolId | null;
}

/**
 * The player intents. There is no "fire-unwitnessed" intent: firing at an
 * unwitnessed call cannot produce a permit round, by construction. A single
 * `fire` intent targets a lane; the reducer permits the front witnessed request
 * in that lane and does nothing constructive for an unwitnessed one.
 */
export type FireIntent =
  | { readonly kind: "tick" }
  | { readonly kind: "fire"; readonly lane: number };

const LCG_A = 1664525;
const LCG_C = 1013904223;

function lcgNext(state: number): number {
  return (Math.imul(state, LCG_A) + LCG_C) >>> 0;
}

/** Deterministically pick a template index for a spawn ordinal and seed. */
export function templateIndexFor(
  seed: number,
  spawnCount: number,
  templateCount: number,
): number {
  if (templateCount <= 0) return 0;
  const salt = ((spawnCount + 1) * 2654435761) >>> 0;
  let s = (seed ^ salt) >>> 0;
  s = lcgNext(s);
  s = lcgNext(s);
  return s % templateCount;
}

/**
 * The legal-move generator: firing on a lane permits the front request there
 * only if it is witnessed. An unwitnessed request offers no permit round, so
 * `firablePermit` returns null for it — that absence is the PERMIT lesson.
 */
export function firablePermit(
  state: FireState,
  lane: number,
): CallRequest | null {
  const front = state.requests
    .filter((r) => r.lane === lane)
    .sort((a, b) => a.distance - b.distance)
    .at(0);
  if (front === undefined) return null;
  return front.witnessed ? front : null;
}

export function frontInLane(
  state: FireState,
  lane: number,
): CallRequest | null {
  const inLane = state.requests
    .filter((r) => r.lane === lane)
    .sort((a, b) => a.distance - b.distance);
  return inLane[0] ?? null;
}

export function waveById(
  waves: readonly FireWave[],
  id: string,
): FireWave | null {
  return waves.find((w) => w.id === id) ?? null;
}

export function createFireState(wave: FireWave, seed = 0x9e3779b9): FireState {
  return {
    waveId: wave.id,
    seed: seed >>> 0,
    tick: 0,
    nextSpawnTick: 0,
    spawnCount: 0,
    requests: [],
    status: "playing",
    permitted: 0,
    breaches: 0,
    lastEvent: "none",
    lastToolId: null,
  };
}

function spawnRequest(wave: FireWave, state: FireState): CallRequest {
  const index = templateIndexFor(
    state.seed,
    state.spawnCount,
    wave.templates.length,
  );
  const template = wave.templates[index] ?? wave.templates[0];
  return {
    id: `${wave.id}-call-${String(state.spawnCount)}`,
    toolId: template.toolId,
    witnessed: template.witnessed,
    lane: template.lane % Math.max(1, wave.lanes),
    distance: wave.laneLength,
  };
}

/** The pure reducer: the single source of truth for which calls execute. */
export function fireReducer(
  wave: FireWave,
  state: FireState,
  intent: FireIntent,
): FireState {
  if (state.status !== "playing") return state;

  if (intent.kind === "fire") {
    const target = firablePermit(state, intent.lane);
    if (target === null) {
      // Either the lane is empty or its front request is unwitnessed. An
      // unwitnessed call has no permit round: firing produces no projectile.
      const front = frontInLane(state, intent.lane);
      return {
        ...state,
        lastEvent: front === null ? state.lastEvent : "no-shot",
        lastToolId: front?.toolId ?? state.lastToolId,
      };
    }
    const requests = state.requests.filter((r) => r.id !== target.id);
    const permitted = state.permitted + 1;
    const status: FireStatus = permitted >= wave.quota ? "cleared" : "playing";
    return {
      ...state,
      requests,
      permitted,
      status,
      lastEvent: "permitted",
      lastToolId: target.toolId,
    };
  }

  // intent.kind === "tick"
  const tick = state.tick + 1;
  let spawnCount = state.spawnCount;
  let nextSpawnTick = state.nextSpawnTick;
  let requests = state.requests.map((r) => ({
    ...r,
    distance: Math.max(0, r.distance - wave.approachPerTick),
  }));

  // Resolve arrivals: a witnessed call that reaches execution unpermitted is a
  // breach (the governed lane should have permitted it); an unwitnessed call
  // that arrives simply never had a permit round, so it passes harmlessly — it
  // was structurally unable to execute.
  let breaches = state.breaches;
  let lastEvent: FireEvent = state.lastEvent;
  let lastToolId: ToolId | null = state.lastToolId;
  const survivors: CallRequest[] = [];
  for (const r of requests) {
    if (r.distance > 0) {
      survivors.push(r);
      continue;
    }
    if (r.witnessed) {
      breaches += 1;
      lastEvent = "breach";
      lastToolId = r.toolId;
    } else {
      lastEvent = "passed";
      lastToolId = r.toolId;
    }
  }
  requests = survivors;

  if (tick >= nextSpawnTick) {
    const fresh = spawnRequest(wave, { ...state, spawnCount, tick });
    requests = [...requests, fresh];
    spawnCount += 1;
    nextSpawnTick = tick + wave.spawnEveryTicks;
  }

  const status: FireStatus =
    breaches >= wave.breachBudget ? "breached" : "playing";
  return {
    ...state,
    tick,
    nextSpawnTick,
    spawnCount,
    requests,
    breaches,
    status,
    lastEvent,
    lastToolId,
  };
}

/**
 * The forbidden generator made explicit for tests and docs: there is no permit
 * round for an unwitnessed tool call. This always reports false — an
 * unwitnessed call cannot be fired into execution, by construction.
 */
export function unwitnessedShotGenerated(): false {
  return false;
}
